"use client";

/**
 * DatePicker — popover + react-day-picker calendar composition (no new Radix root).
 * The trigger renders the formatted selected date (date-fns) or a placeholder.
 * D-09 states inherit from the vendored popover/calendar primitives' tokens.
 */
import * as React from "react";
import { CalendarIcon } from "lucide-react";
import { format } from "date-fns";

import { cn } from "../lib/utils";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export interface DatePickerProps {
  value?: Date;
  onValueChange?: (date: Date | undefined) => void;
  placeholder?: string;
  /** Initial month shown in the calendar (used for deterministic tests). */
  defaultMonth?: Date;
  disabled?: boolean;
  className?: string;
}

function DatePicker({
  value: controlledValue,
  onValueChange,
  placeholder = "Pick a date",
  defaultMonth,
  disabled,
  className,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [internalValue, setInternalValue] = React.useState<Date | undefined>(
    undefined,
  );
  const value = controlledValue ?? internalValue;

  function select(next: Date | undefined) {
    if (controlledValue === undefined) setInternalValue(next);
    onValueChange?.(next);
    if (next) setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          aria-label={value ? format(value, "PPP") : placeholder}
          disabled={disabled}
          className={cn(
            "w-[240px] justify-start text-left font-medium",
            !value && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="mr-2 size-4" />
          {value ? format(value, "PPP") : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={value}
          onSelect={select}
          defaultMonth={defaultMonth ?? value}
          autoFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export { DatePicker };
